import React, { useEffect, useMemo, useState } from 'react';

import { listSiteListAudit } from './operations';
import { auditFieldChanges, formatWhen, toAuditCsvData } from './utils';
import { ExportCsvButton, ModalShell } from '../../components';
import { newDateFormatted } from '../../utils';

import './styles.scoped.css';

/**
 * CP-857: SiteList change history.
 *
 * Satisfies the audit-trail requirement — who changed what, when, and old -> new. Opened
 * either for the whole table (toolbar) or scoped to one row (from the grid), which is why it
 * takes an optional `scope`.
 *
 * Deletes are the reason this exists: SiteList is hard-deleted so BI keeps reading it with
 * no IsActive filter, and this is where a removed row's values are recovered from.
 *
 * Exportable, because the reason to read a quarter of history is usually to feed it to
 * something else — "who was the SL on this branch when the quarter closed" is answered from
 * the file, not by scrolling this table.
 */

// A blank value has to render as something visible, or "cleared the AM" looks like nothing
// happened at all.
const BLANK = '—';

/**
 * One "Field: before → after" line per changed field.
 *
 * The field NAMES alone are not enough to answer the question people open this for ("what
 * did the wage used to be?"), so the values are shown. Only changed fields appear, so an
 * edit to one column does not print ten unchanged ones.
 */
const ChangeDetail = ({ entry }) => {
  const changes = auditFieldChanges(entry);

  if (!changes.length) {
    return <span className="audit-nochange">no field values changed</span>;
  }

  return (
    <ul className="audit-changes">
      {changes.map(({ label, from, to }) => (
        <li key={label}>
          <span className="audit-field">{label}</span>
          {from !== null && <span className="audit-from">{from || BLANK}</span>}
          {from !== null && to !== null && <span className="audit-arrow">→</span>}
          {to !== null && <span className="audit-to">{to || BLANK}</span>}
        </li>
      ))}
    </ul>
  );
};

// sp_cl_cp_bi_sitelist_audit_list caps its result at @Top, defaulting to 500, so an
// unbounded history cannot time out the API Lambda. Hitting the cap has to be visible:
// a bulk import writes one audit row per record, so two full imports reach it, and an
// export that silently held only the most recent 500 changes would be read as complete.
const AUDIT_ROW_CAP = 500;

const AuditModal = ({ scope, onClose }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    listSiteListAudit({ branchNumber: scope?.BranchNumber, lob: scope?.LOB })
      .then(setEntries)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [scope?.BranchNumber, scope?.LOB]);

  const csvData = useMemo(() => toAuditCsvData(entries), [entries]);
  const csvFileName = useMemo(
    () => `sitelist-history-${scope ? `branch-${scope.BranchNumber}-` : ''}${newDateFormatted.toString()}.csv`,
    [scope],
  );

  return (
    <ModalShell
      title="SiteList Change History"
      subtitle={scope ? `Branch ${scope.BranchNumber} / ${scope.LOB}` : 'Most recent changes across the whole list.'}
      buttons={[{ text: 'Close', onPress: onClose }]}>
      {loading && <p className="audit-empty">Loading…</p>}

      {!loading && !!error && <p className="form-error">{error}</p>}

      {!loading && !error && !entries.length && <p className="audit-empty">No changes recorded yet.</p>}

      {!loading && !error && !!entries.length && (
        <div className="audit-toolbar">
          <p className="audit-count">
            {entries.length} change{entries.length === 1 ? '' : 's'}
            {entries.length >= AUDIT_ROW_CAP && ` (the most recent ${AUDIT_ROW_CAP} — older changes are not shown)`}
          </p>
          {/* One CSV row per changed field, so "when did the SL change" is a filter rather
              than a diff. See toAuditCsvData. */}
          <ExportCsvButton data={csvData} filename={csvFileName} />
        </div>
      )}

      {!loading && !error && !!entries.length && (
        <div className="audit-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>Action</th>
                <th>Branch</th>
                <th>LOB</th>
                <th>Change</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(entry => (
                <tr key={entry.AuditId}>
                  <td>{formatWhen(entry.ChangedAt)}</td>
                  <td>{entry.ChangedBy}</td>
                  <td>{entry.ChangeAction}</td>
                  <td>{entry.BranchNumber}</td>
                  <td>{entry.LOB}</td>
                  <td>
                    <ChangeDetail entry={entry} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ModalShell>
  );
};

export default AuditModal;
